/**
 * Parses mesh-llm's skippy-model-package `write-package` output
 * (model-package.json: a "layer-package" manifest — shared/{metadata,
 * embeddings,output}.gguf + layers/layer-NNNNN.gguf, one file per
 * transformer layer, each with a SHA-256 in the manifest) and resolves the
 * fragment set a stage covering [layerStart, layerEnd) actually needs to
 * download.
 *
 * See docs/SLICING.md for the artifact-slice design and the tied-embedding
 * gotcha fragmentsForRange encodes below.
 */
function isArtifactChunkMeta(v) {
    if (typeof v !== 'object' || v === null)
        return false;
    const o = v;
    return (typeof o.path === 'string' &&
        typeof o.sha256 === 'string' &&
        o.sha256.length === 64 &&
        typeof o.bytes === 'number');
}
function isArtifactMeta(v) {
    if (typeof v !== 'object' || v === null)
        return false;
    const o = v;
    if (!(typeof o.path === 'string' &&
        typeof o.tensor_count === 'number' &&
        typeof o.tensor_bytes === 'number' &&
        typeof o.artifact_bytes === 'number' &&
        typeof o.sha256 === 'string' &&
        o.sha256.length === 64)) {
        return false;
    }
    if (o.chunks !== undefined) {
        if (!Array.isArray(o.chunks))
            return false;
        if (!o.chunks.every(isArtifactChunkMeta))
            return false;
    }
    return true;
}
/** Validates and narrows a parsed model-package.json into a LayerPackageManifest. Throws with a specific reason on any shape mismatch. */
export function parseLayerPackageManifest(json) {
    if (typeof json !== 'object' || json === null) {
        throw new Error('layer-package manifest must be a JSON object');
    }
    const o = json;
    if (o.format !== 'layer-package') {
        throw new Error(`layer-package manifest has unexpected format ${JSON.stringify(o.format)} (expected "layer-package")`);
    }
    if (typeof o.layer_count !== 'number' || o.layer_count <= 0) {
        throw new Error('layer-package manifest missing a positive layer_count');
    }
    if (typeof o.activation_width !== 'number' || o.activation_width <= 0) {
        throw new Error('layer-package manifest missing a positive activation_width');
    }
    if (typeof o.shared !== 'object' || o.shared === null) {
        throw new Error('layer-package manifest missing shared{}');
    }
    const shared = o.shared;
    for (const key of ['metadata', 'embeddings', 'output']) {
        if (!isArtifactMeta(shared[key])) {
            throw new Error(`layer-package manifest shared.${key} is missing or malformed`);
        }
    }
    if (!Array.isArray(o.layers)) {
        throw new Error('layer-package manifest missing layers[]');
    }
    const layers = [];
    for (const entry of o.layers) {
        if (!isArtifactMeta(entry) || typeof entry.layer_index !== 'number') {
            throw new Error('layer-package manifest has a malformed layers[] entry');
        }
        layers.push(entry);
    }
    if (layers.length !== o.layer_count) {
        throw new Error(`layer-package manifest layer_count=${o.layer_count} but layers[] has ${layers.length} entries`);
    }
    return {
        schema_version: typeof o.schema_version === 'number' ? o.schema_version : 1,
        model_id: typeof o.model_id === 'string' ? o.model_id : '',
        format: 'layer-package',
        layer_count: o.layer_count,
        activation_width: o.activation_width,
        kv_bytes_per_token_per_layer: typeof o.kv_bytes_per_token_per_layer === 'number' ? o.kv_bytes_per_token_per_layer : undefined,
        driver_layers: typeof o.driver_layers === 'number' ? o.driver_layers : undefined,
        shared: shared,
        layers,
    };
}
/**
 * True when the model ties input/output embeddings (no separate
 * output.weight tensor — the final stage's lm_head matmul reuses
 * token_embd.weight via llama-model.cpp's TENSOR_DUPLICATED fallback).
 *
 * Detected from shared.output.tensor_count: an untied model's output
 * artifact holds two tensors (output_norm.weight + output.weight); a tied
 * model's holds only output_norm.weight. This is a real, verified-on-Qwen3
 * distinction, not a guess — see the 0100 patch header and
 * docs/SLICING.md for the empirical finding (a tied-embedding output-only
 * stage that omits the shared embeddings artifact fails to load with
 * "missing tensor 'token_embd.weight'").
 */
export function manifestTiesEmbeddings(manifest) {
    return manifest.shared.output.tensor_count < 2;
}
function toFragment(baseUrl, meta, role) {
    const url = baseUrl ? new URL(meta.path, baseUrl).toString() : meta.path;
    const fragment = { url, sha256: meta.sha256, bytes: meta.artifact_bytes, role };
    if (meta.chunks && meta.chunks.length > 0) {
        fragment.chunks = meta.chunks.map((c) => ({
            url: baseUrl ? new URL(c.path, baseUrl).toString() : c.path,
            sha256: c.sha256,
            bytes: c.bytes,
        }));
    }
    // Layer fragments carry a per-tensor offset index (LayerPackageLayerMeta);
    // the base LayerPackageArtifactMeta param doesn't type it, so read it off.
    const tensors = meta.tensors;
    if (role === 'layer' && tensors && tensors.length > 0) {
        fragment.tensors = tensors;
    }
    return fragment;
}
/**
 * Resolves the fragment set a stage covering [layerStart, layerEnd) needs
 * to download, in skippy_model_open_from_parts order (paths[0] must carry
 * full model metadata/hparams/vocab — see the 0100 patch / skippy.cpp's
 * ordered_parts loader, which reads hparams only from the first path).
 *
 * Order: shared/metadata.gguf, [shared/embeddings.gguf if isFirst],
 * layers/layer-NNNNN.gguf for each layer in [layerStart, layerEnd),
 * [shared/output.gguf if isFinal], [shared/embeddings.gguf if isFinal AND
 * the model ties embeddings AND it wasn't already added for isFirst — a
 * one-stage/monolith-via-package descriptor is both].
 *
 * `manifestBaseUrl` should point at the manifest's own location (the
 * package root) — every artifact `path` in the manifest is relative to it.
 */
export function fragmentsForRange(manifest, manifestBaseUrl, layerStart, layerEnd, isFirst, isFinal, 
/**
 * INCREMENTAL LOAD (#47): when true AND the manifest carries
 * `shared.metadata_index`, the 'metadata'-role fragment points at the
 * header-only TYPE-index GGUF (metadata-index.gguf) that begin() needs,
 * instead of the KV-only metadata.gguf. The monolithic path passes false
 * (default) and keeps getting metadata.gguf — which it MUST, since it loads
 * every fragment as one multi-part GGUF and metadata.gguf is KV-only there.
 */
useMetadataIndex = false) {
    if (!Number.isInteger(layerStart) || !Number.isInteger(layerEnd) || layerStart < 0 || layerEnd <= layerStart) {
        throw new RangeError(`invalid layer range [${layerStart}, ${layerEnd})`);
    }
    if (layerEnd > manifest.layer_count) {
        throw new RangeError(`layerEnd=${layerEnd} exceeds manifest layer_count=${manifest.layer_count}`);
    }
    const byIndex = new Map(manifest.layers.map((l) => [l.layer_index, l]));
    // Incremental begin() needs the TYPE index; the monolithic path needs the
    // KV-only metadata. Same 'metadata' role slot, different source artifact.
    const metadataMeta = useMetadataIndex && isArtifactMeta(manifest.shared.metadata_index)
        ? manifest.shared.metadata_index
        : manifest.shared.metadata;
    const fragments = [toFragment(manifestBaseUrl, metadataMeta, 'metadata')];
    let embeddingsIncluded = false;
    if (isFirst) {
        fragments.push(toFragment(manifestBaseUrl, manifest.shared.embeddings, 'embeddings'));
        embeddingsIncluded = true;
    }
    for (let i = layerStart; i < layerEnd; i++) {
        const meta = byIndex.get(i);
        if (!meta)
            throw new Error(`layer-package manifest has no entry for layer_index=${i}`);
        fragments.push(toFragment(manifestBaseUrl, meta, 'layer'));
    }
    if (isFinal) {
        fragments.push(toFragment(manifestBaseUrl, manifest.shared.output, 'output'));
        if (!embeddingsIncluded && manifestTiesEmbeddings(manifest)) {
            fragments.push(toFragment(manifestBaseUrl, manifest.shared.embeddings, 'embeddings'));
        }
    }
    return fragments;
}
